import { createContext, useState, useEffect } from 'react';
import type { User } from 'firebase/auth';

import { onAuthStateChanged } from 'firebase/auth';
import { auth } from 'utils/firebase';

import { removeAuthInfoFromStorage } from './storage';

interface IAuthContext {
  // undefined -> ログイン未チェック状態
  // User -> ログイン済
  // null -> 未ログイン
  currentUser: User | null | undefined;
  // メールアドレスでログイン済か（クライアント用チャット画面の匿名ログインは含めない）
  isLoggedIn: boolean;
  isAuthChecking: boolean;
  updateCurrentUser: (user: User | null | undefined) => void;
}

const initialContext: IAuthContext = {
  currentUser: undefined,
  isLoggedIn: false,
  isAuthChecking: true,
  updateCurrentUser: () => {} /* eslint-disable-line @typescript-eslint/no-empty-function */,
};

interface Props {
  children: JSX.Element;
}

export const AuthContext = createContext<IAuthContext>(initialContext);

/**
 * ログイン状態は Firebase Authentication のみで判定する。
 * （旧APIの数値ユーザーIDは使わない）
 */
export const AuthProvider = ({ children }: Props): JSX.Element => {
  const [currentUser, setCurrentUser] = useState<User | null | undefined>(undefined);
  const [isAuthChecking, setIsAuthChecking] = useState<boolean>(initialContext.isAuthChecking);

  const updateCurrentUser = (user: User | null | undefined) => {
    setCurrentUser(user);
  };

  useEffect(() => {
    // 旧ログイン方式でローカルストレージに保存していたユーザーIDを削除する
    removeAuthInfoFromStorage();

    // firebase認証状態が自動的に行われる
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      // チェック完了にする
      setIsAuthChecking(false);
    });
    return unsubscribe;
  }, []);

  const isLoggedIn = !!currentUser && !currentUser.isAnonymous;

  return (
    <AuthContext.Provider value={{ currentUser, isLoggedIn, isAuthChecking, updateCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};
